import { firmDocumentsUrl } from 'common/config/urls';
import {
  clickGuidedTourTarget,
  closeOpenGuidedTourMenus,
  getGuidedTourTestIdSelector,
  GUIDED_TOUR_STEP_TRIGGER_PREVIEW_MS,
  type GuidedTourDefinition,
  hasVisibleGuidedTourElement,
  waitForVisibleGuidedTourElement,
} from 'components/ui';
import { ALL_COMPANIES_PANEL_TEST_IDS } from 'pages/Documents/FirmDocuments/components/AllCompaniesPanel/AllCompaniesPanel.testIds';
import { FIRM_DOCUMENT_PREVIEW_PANEL_TEST_IDS } from 'pages/Documents/FirmDocuments/components/FirmDocumentPreviewPanel/FirmDocumentPreviewPanel.testIds';
import { MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS } from 'pages/Documents/FirmDocuments/components/MeasurementDateDocumentsTree/MeasurementDateDocumentsTree.testIds';
import { FIRM_DOCUMENTS_TEST_IDS } from 'pages/Documents/FirmDocuments/FirmDocuments.testIds';

export const FIRM_DOCUMENTS_TOUR_ID = 'firm-documents';

const companyPanelSelector = getGuidedTourTestIdSelector(ALL_COMPANIES_PANEL_TEST_IDS.root);
const documentScopeTabSelector = getGuidedTourTestIdSelector('tertiary-navigation__item-all-documents');
const documentActionsSelector = getGuidedTourTestIdSelector('tertiary-navigation__actions');
const documentRowSelector = `[data-testid^="${MEASUREMENT_DATE_DOCUMENTS_TREE_TEST_IDS.filePrefix}"]`;
const documentSearchSelector = getGuidedTourTestIdSelector(FIRM_DOCUMENTS_TEST_IDS.documentSearchInput);
const documentSearchRootSelector = getGuidedTourTestIdSelector(`${FIRM_DOCUMENTS_TEST_IDS.documentSearchInput}-root`);
const documentSearchTriggerSelector = getGuidedTourTestIdSelector(
  `${FIRM_DOCUMENTS_TEST_IDS.documentSearchInput}-trigger`
);
const filterButtonSelector = getGuidedTourTestIdSelector(FIRM_DOCUMENTS_TEST_IDS.filterButton);
const filterDialogPaperSelector = getGuidedTourTestIdSelector(FIRM_DOCUMENTS_TEST_IDS.filterDialogPaper);
const fullscreenButtonSelector = getGuidedTourTestIdSelector(FIRM_DOCUMENTS_TEST_IDS.fullscreenButton);
const measurementDateAddSubfolderSelector = '[data-testid^="firm-documents-measurement-date-add-subfolder-"]';
const measurementDateHeaderSelector = '[data-measurement-dates-accordion-panel-item="true"]';
const measurementDateUploadSelector = '[data-testid^="firm-documents-measurement-date-upload-document-"]';
const measurementDateActionSelector = `:is(${measurementDateAddSubfolderSelector}, ${measurementDateUploadSelector})`;
const previewActionsSelector = getGuidedTourTestIdSelector(FIRM_DOCUMENT_PREVIEW_PANEL_TEST_IDS.actions);
const previewActionsMenuSelector = `#${FIRM_DOCUMENT_PREVIEW_PANEL_TEST_IDS.actions}-menu .MuiPopover-paper`;
const previewActionsMenuTriggerSelector = `${previewActionsSelector} [data-file-row-actions-menu-trigger="true"]`;
const previewCloseSelector = getGuidedTourTestIdSelector(FIRM_DOCUMENT_PREVIEW_PANEL_TEST_IDS.closeAction);
const previewExpandSelector = getGuidedTourTestIdSelector(FIRM_DOCUMENT_PREVIEW_PANEL_TEST_IDS.expandAction);
const previewPanelSelector = getGuidedTourTestIdSelector(FIRM_DOCUMENT_PREVIEW_PANEL_TEST_IDS.root);
const resizeHandleSelector = getGuidedTourTestIdSelector(FIRM_DOCUMENTS_TEST_IDS.leftResizeHandle);
const selectAllSelector = getGuidedTourTestIdSelector(FIRM_DOCUMENTS_TEST_IDS.selectAllDocuments);

const openDocumentsSearch = async () => {
  if (hasVisibleGuidedTourElement(documentSearchRootSelector)) {
    return;
  }

  await clickGuidedTourTarget(documentSearchTriggerSelector);
};

const openDocumentsFilter = async () => {
  if (hasVisibleGuidedTourElement(filterDialogPaperSelector)) {
    return;
  }

  await clickGuidedTourTarget(filterButtonSelector);
};

const getVisibleDocumentRow = () =>
  Array.from(document.querySelectorAll<HTMLElement>(documentRowSelector)).find(row => {
    const rect = row.getBoundingClientRect();

    return rect.width > 0 && rect.height > 0;
  });

const hasVisibleDocumentRow = () => Boolean(getVisibleDocumentRow());

const openDocumentPreview = async () => {
  if (hasVisibleGuidedTourElement(previewPanelSelector)) {
    return;
  }

  const documentRowTestId = getVisibleDocumentRow()?.getAttribute('data-testid');

  if (!documentRowTestId) {
    return;
  }

  await clickGuidedTourTarget(getGuidedTourTestIdSelector(documentRowTestId));
  await waitForVisibleGuidedTourElement(previewPanelSelector, { timeoutMs: 6000 });
};

const canOpenPreviewActionsMenu = () => {
  const menuTrigger = document.querySelector<HTMLButtonElement>(previewActionsMenuTriggerSelector);

  return Boolean(
    menuTrigger && !menuTrigger.disabled && hasVisibleGuidedTourElement(previewActionsMenuTriggerSelector)
  );
};

const openPreviewActionsMenu = async () => {
  if (hasVisibleGuidedTourElement(previewActionsMenuSelector)) {
    return;
  }

  await clickGuidedTourTarget(previewActionsMenuTriggerSelector);
  await waitForVisibleGuidedTourElement(previewActionsMenuSelector);
};

const prepareDocumentPreviewActions = async () => {
  await openDocumentPreview();

  if (canOpenPreviewActionsMenu()) {
    await openPreviewActionsMenu();
  }
};

const waitForDocumentsWorkspace = () => waitForVisibleGuidedTourElement(documentScopeTabSelector, { timeoutMs: 6000 });

export const getFirmDocumentsTour = (firmSlug?: string): GuidedTourDefinition => ({
  description:
    'Learn the core controls for firm documents, company scope, search, filters, previews, and measurement dates.',
  id: FIRM_DOCUMENTS_TOUR_ID,
  steps: [
    {
      beforeStep: waitForDocumentsWorkspace,
      body: 'Start in All Documents for the firm-wide view, or open a company tab to focus on one company.',
      id: 'documents-workspace',
      placement: 'bottom',
      route: firmSlug ? firmDocumentsUrl(firmSlug) : undefined,
      target: documentScopeTabSelector,
      title: 'Document scope',
      waitForTargetBeforeShowing: true,
      waitForTargetTimeoutMs: 6000,
    },
    {
      body: 'Use the company panel to find companies, filter by fund, and open company tabs.',
      highlightTargets: [companyPanelSelector],
      id: 'documents-company-panel',
      placement: 'right',
      target: companyPanelSelector,
      title: 'Company navigation',
    },
    {
      body: 'Drag the divider when company names, document metadata, or folder depth need more room.',
      canShow: () => hasVisibleGuidedTourElement(resizeHandleSelector),
      highlightTargets: [resizeHandleSelector],
      id: 'documents-resize',
      placement: 'right',
      target: resizeHandleSelector,
      title: 'Resize the layout',
    },
    {
      body: 'Use these controls to select documents, search, filter, and expand the workspace.',
      canShow: () =>
        hasVisibleGuidedTourElement(documentActionsSelector) ||
        hasVisibleGuidedTourElement(selectAllSelector) ||
        hasVisibleGuidedTourElement(filterButtonSelector) ||
        hasVisibleGuidedTourElement(fullscreenButtonSelector),
      id: 'documents-page-actions',
      placement: 'bottom',
      target: documentActionsSelector,
      title: 'Document controls',
    },
    {
      actionTarget: documentSearchTriggerSelector,
      afterStep: closeOpenGuidedTourMenus,
      beforeStep: openDocumentsSearch,
      beforeStepDelayMs: GUIDED_TOUR_STEP_TRIGGER_PREVIEW_MS,
      body: 'Search suggests matching documents as you type. Press Enter to search the selected scope.',
      canShow: () =>
        hasVisibleGuidedTourElement(documentSearchRootSelector) ||
        hasVisibleGuidedTourElement(documentSearchSelector) ||
        hasVisibleGuidedTourElement(documentSearchTriggerSelector),
      contentReadyDelayMs: 0,
      highlightTargets: [documentSearchRootSelector],
      id: 'documents-search',
      placement: 'bottom',
      spotlightPadding: 6,
      target: documentSearchRootSelector,
      targetTrackingDurationMs: 360,
      title: 'Search documents',
    },
    {
      actionTarget: filterButtonSelector,
      afterStep: closeOpenGuidedTourMenus,
      beforeStep: openDocumentsFilter,
      beforeStepDelayMs: GUIDED_TOUR_STEP_TRIGGER_PREVIEW_MS,
      body: 'Filter by source, reference type, document type, or sort order.',
      canShow: () => hasVisibleGuidedTourElement(filterButtonSelector),
      contentReadyDelayMs: 160,
      highlightTargets: [filterDialogPaperSelector],
      id: 'documents-filters',
      placement: 'right',
      target: filterDialogPaperSelector,
      title: 'Filter and sort',
    },
    {
      body: 'Documents are organized by measurement date. In All Documents, company groups keep ownership visible.',
      canShow: () => hasVisibleGuidedTourElement(measurementDateHeaderSelector),
      id: 'documents-tree',
      placement: 'bottom',
      target: measurementDateHeaderSelector,
      title: 'Measurement-date structure',
    },
    {
      actionTarget: documentRowSelector,
      beforeStep: openDocumentPreview,
      beforeStepDelayMs: GUIDED_TOUR_STEP_TRIGGER_PREVIEW_MS,
      body: 'Select a file to review its contents without leaving Documents.',
      canShow: () => hasVisibleGuidedTourElement(previewPanelSelector) || hasVisibleDocumentRow(),
      contentReadyDelayMs: 160,
      highlightTargets: [previewPanelSelector],
      id: 'documents-preview',
      placement: 'left',
      target: previewPanelSelector,
      title: 'Document preview',
      waitForTargetBeforeShowing: true,
      waitForTargetTimeoutMs: 6000,
    },
    {
      afterStep: closeOpenGuidedTourMenus,
      beforeStep: prepareDocumentPreviewActions,
      beforeStepDelayMs: GUIDED_TOUR_STEP_TRIGGER_PREVIEW_MS,
      body: 'Download, copy, rename, or delete when available. Expand the preview when you need more room.',
      canShow: () => hasVisibleGuidedTourElement(previewPanelSelector) || hasVisibleDocumentRow(),
      contentReadyDelayMs: 160,
      highlightTargets: [
        previewActionsSelector,
        previewActionsMenuSelector,
        previewExpandSelector,
        previewCloseSelector,
      ],
      id: 'documents-preview-actions',
      placement: 'left',
      target: previewActionsSelector,
      title: 'Preview actions',
      waitForTargetBeforeShowing: true,
      waitForTargetTimeoutMs: 6000,
    },
    {
      body: 'Use measurement date actions to add folders or upload documents.',
      canShow: () =>
        hasVisibleGuidedTourElement(measurementDateAddSubfolderSelector) ||
        hasVisibleGuidedTourElement(measurementDateUploadSelector),
      highlightTargets: [measurementDateAddSubfolderSelector, measurementDateUploadSelector],
      id: 'documents-upload',
      placement: 'top',
      target: measurementDateActionSelector,
      title: 'Measurement date actions',
    },
  ],
  title: 'Firm Documents',
});

export const firmDocumentsTour = getFirmDocumentsTour();
